// Frontend Security Utilities

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return input
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/"/g, '"')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with strength score
 */
export const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const checks = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar];
  const passedChecks = checks.filter(Boolean).length;

  let strength = 'weak';
  if (password.length >= minLength && passedChecks >= 3) {
    strength = 'strong';
  } else if (password.length >= minLength && passedChecks >= 2) {
    strength = 'medium';
  }

  return {
    isValid: password.length >= minLength && passedChecks >= 2,
    strength,
    checks: {
      length: password.length >= minLength,
      uppercase: hasUpperCase,
      lowercase: hasLowerCase,
      numbers: hasNumbers,
      special: hasSpecialChar
    }
  };
};

/**
 * Generates a secure random string
 * @param {number} length - Length of the string
 * @returns {string} - Random string
 */
export const generateSecureRandom = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Secure localStorage wrapper with encryption
 */
export class SecureStorage {
  static setItem(key, value) {
    try {
      const encrypted = btoa(JSON.stringify(value));
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error('SecureStorage setItem error:', error);
    }
  }

  static getItem(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;
      return JSON.parse(atob(encrypted));
    } catch (error) {
      console.error('SecureStorage getItem error:', error);
      return null;
    }
  }

  static removeItem(key) {
    localStorage.removeItem(key);
  }

  static clear() {
    localStorage.clear();
  }
}

/**
 * Content Security Policy violation handler
 */
export const handleCSPViolation = (event) => {
  console.warn('CSP Violation:', {
    violatedDirective: event.violatedDirective,
    blockedURI: event.blockedURI,
    sourceFile: event.sourceFile,
    lineNumber: event.lineNumber,
    columnNumber: event.columnNumber
  });

  // Report to monitoring service
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'csp_violation', {
      event_category: 'security',
      event_label: event.violatedDirective,
      value: 1
    });
  }
};

/**
 * Sets up security event listeners
 */
export const setupSecurityListeners = () => {
  if (typeof document === 'undefined') return;

  // CSP violation listener
  document.addEventListener('securitypolicyviolation', handleCSPViolation);

  // Prevent context menu on production
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      return false;
    });
  }

  // Prevent drag and drop of external files
  document.addEventListener('dragover', (e) => {
    e.preventDefault();
  });

  document.addEventListener('drop', (e) => {
    e.preventDefault();
  });
};

/**
 * Validates file before upload
 * @param {File} file - File to validate
 * @returns {object} - Validation result
 */
export const validateFile = (file) => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const errors = [];

  if (!allowedTypes.includes(file.type)) {
    errors.push('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
  }

  if (file.size > maxSize) {
    errors.push('File too large. Maximum size is 5MB.');
  }

  // Check for suspicious filename
  const suspiciousChars = /[<>\"'|?*]/;
  if (suspiciousChars.test(file.name)) {
    errors.push('Invalid filename. Please use only letters, numbers, and safe characters.');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Rate limiting for frontend actions
 */
export class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }

  canMakeRequest() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);

    if (this.requests.length >= this.maxRequests) {
      return false;
    }

    this.requests.push(now);
    return true;
  }

  getRemainingTime() {
    if (this.requests.length === 0) return 0;
    const oldestRequest = Math.min(...this.requests);
    return Math.max(0, this.windowMs - (Date.now() - oldestRequest));
  }
}

// Global rate limiter for API calls
export const apiRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute

/**
 * Secure API call wrapper
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise} - API response
 */
export const secureApiCall = async (url, options = {}) => {
  if (!apiRateLimiter.canMakeRequest()) {
    throw new Error('Too many requests. Please wait before trying again.');
  }

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers
    },
    credentials: 'include',
    ...options
  };

  try {
    const response = await fetch(url, defaultOptions);

    // Check for security-related status codes
    if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    if (response.status === 403) {
      // Possible CSRF or auth issue
      console.warn('403 Forbidden - possible security issue');
    }

    return response;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};